import { useEffect, useMemo, useState } from 'react';

import { fetchQuery, graphql } from 'react-relay';

import fireErrorAnalytics from '@atlassian/error-handling/fire-error-analytics';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useHubContext } from '../../../../utils';

import type {
	useSupportedLanguagesQuery,
	useSupportedLanguagesQuery$data,
} from './__generated__/useSupportedLanguagesQuery.graphql';

/**
 * A language option keyed by locale tag, carrying a pre-formatted display name.
 *
 * This mirrors the data-driven shape used by the Help Center announcement
 * (home page announcement) language picker (`Record<localeTag, { localeDisplayName }>`),
 * so the manage translations selector can be populated from the backend with identical logic.
 */
export type SupportedLanguageRecord = Record<string, { localeDisplayName: string }>;

export interface UseSupportedLanguagesResult {
	/** The locale tag of the site's default language, if known. */
	defaultLocale: string;
	/** Whether the backend request is in flight. */
	isLoading: boolean;
	/** The supported languages, keyed by locale tag. */
	languages: SupportedLanguageRecord;
}

/**
 * Merges the raw name/description translation entries returned by the Help Center
 * AGG into a `Record<localeTag, { localeDisplayName }>`.
 *
 * This is the same merge the Help Center announcement (home page announcement) relay
 * connector uses to build its language picker options: every locale that appears in the
 * home page announcement translations contributes an option, labelled by its
 * `localeDisplayName`.
 */
const buildLanguageRecord = (
	response: useSupportedLanguagesQuery$data | undefined,
): { defaultLocale: string; languages: SupportedLanguageRecord } => {
	const data = response?.helpCenter?.helpCenterById;

	if (data?.__typename !== 'HelpCenter') {
		return { defaultLocale: '', languages: {} };
	}

	const defaultLocale = data.siteDefaultLanguageTag ?? '';
	const languages: SupportedLanguageRecord = {};

	const homePageAnnouncements = data.announcements?.homePageAnnouncements ?? [];
	const translationEntries = homePageAnnouncements.flatMap((announcement) => [
		...(announcement?.nameTranslationsRaw ?? []),
		...(announcement?.descriptionTranslationsRaw ?? []),
	]);

	translationEntries.forEach((entry) => {
		if (entry?.locale && entry.localeDisplayName) {
			languages[entry.locale] = { localeDisplayName: entry.localeDisplayName };
		}
	});

	return { defaultLocale, languages };
};

/**
 * Fetches the set of languages available for translation from the backend, using the
 * same logic and query shape as the Help Center announcement (home page announcement)
 * language picker (`helpCenter(cloudId).helpCenterById(helpCenterAri)` →
 * `siteDefaultLanguageTag` and each translation's `{ locale, localeDisplayName }`).
 *
 * The result is a `Record<localeTag, { localeDisplayName }>` that the manage translations
 * selector maps into `{ value, label }` options, filtering out the default and any
 * already-used locale.
 */
export const useSupportedLanguages = (): UseSupportedLanguagesResult => {
	const environment = useRelayEnvironmentProvider().getEnvironment({});
	const {
		cloudId,
		env: { helpCenterAri },
	} = useHubContext();

	const [languages, setLanguages] = useState<SupportedLanguageRecord>({});
	const [defaultLocale, setDefaultLocale] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!cloudId || !helpCenterAri) {
			return;
		}

		setIsLoading(true);

		const subscription = fetchQuery<useSupportedLanguagesQuery>(
			environment,
			graphql`
				query useSupportedLanguagesQuery($cloudId: ID!, $helpCenterAri: ID!) {
					helpCenter(cloudId: $cloudId) {
						helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: ["HelpCenterAggBeta"]) {
							... on HelpCenter {
								__typename
								siteDefaultLanguageTag
								announcements {
									homePageAnnouncements {
										nameTranslationsRaw {
											locale
											localeDisplayName
										}
										descriptionTranslationsRaw {
											locale
											localeDisplayName
										}
									}
								}
							}
							... on QueryError {
								__typename
							}
						}
					}
				}
			`,
			{ cloudId, helpCenterAri },
			{
				fetchPolicy: 'store-or-network',
			},
		).subscribe({
			next: (response) => {
				const { defaultLocale: nextDefaultLocale, languages: nextLanguages } =
					buildLanguageRecord(response);
				setDefaultLocale(nextDefaultLocale);
				setLanguages(nextLanguages);
			},
			error: (error: unknown) => {
				setIsLoading(false);
				fireErrorAnalytics({
					error: error instanceof Error ? error : new Error('Failed to fetch supported languages'),
					meta: {
						id: 'useSupportedLanguages',
						packageName: '@atlassian/company-hub-common',
					},
				});
			},
			complete: () => {
				setIsLoading(false);
			},
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [environment, cloudId, helpCenterAri]);

	return useMemo(
		() => ({ defaultLocale, isLoading, languages }),
		[defaultLocale, isLoading, languages],
	);
};
